import posthog from 'posthog-js';

export type AnalyticsPage = 'overview' | 'systems' | 'issues' | 'binsr_draft' | 'client_prep';
export type Severity = 'critical' | 'moderate' | 'minor';

type AnalyticsContext = {
  page: AnalyticsPage;
  ui_variant: 'desktop' | 'mobile';
  report_shape: 'small' | 'medium' | 'large';
  severity_mix: string;
  selected_issue_count: number;
};

type AnalyticsProperties = Record<string, string | number | boolean | undefined>;

let isInitialized = false;
let context: AnalyticsContext | null = null;

const isBuyerShareRoute = () => window.location.pathname.startsWith('/share/');

export const redactAnalyticsUrl = (
  value: string | undefined,
  origin = typeof window === 'undefined' ? 'https://app.clearcasa.test' : window.location.origin,
): string | undefined => {
  if (!value) return value;

  try {
    const url = new URL(value, origin);
    url.pathname = url.pathname
      .replace(/^\/a\/[^/]+/, '/a/[REDACTED]')
      .replace(/^\/share\/[^/]+/, '/share/[REDACTED]');
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return value
      .replace(/(\/a\/)[^/?#]+/, '$1[REDACTED]')
      .replace(/(\/share\/)[^/?#]+/, '$1[REDACTED]')
      .split('?')[0]
      .split('#')[0];
  }
};

export const getReportShape = (issueCount: number): AnalyticsContext['report_shape'] => {
  if (issueCount <= 10) return 'small';
  if (issueCount <= 30) return 'medium';
  return 'large';
};

export const getSeverityMix = (counts: Record<Severity, number>): string => (
  `critical:${counts.critical};moderate:${counts.moderate};minor:${counts.minor}`
);

export const initializeAnalytics = (): void => {
  if (isInitialized || !__POSTHOG_ENABLED__ || !__POSTHOG_PROJECT_TOKEN__ || isBuyerShareRoute()) return;

  posthog.init(__POSTHOG_PROJECT_TOKEN__, {
    api_host: 'https://us.i.posthog.com',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_dead_clicks: false,
    capture_exceptions: false,
    capture_heatmaps: false,
    capture_performance: false,
    disable_capture_url_hashes: true,
    enable_recording_console_log: false,
    person_profiles: 'never',
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: redactAnalyticsUrl(request.name) || '[REDACTED]',
      }),
    },
    before_send: (event) => {
      if (!event || isBuyerShareRoute()) return null;

      event.properties = {
        ...event.properties,
        $current_url: redactAnalyticsUrl(window.location.href) || '[REDACTED]',
      };
      return event;
    },
  });

  // Replay is intentionally started only after the privacy configuration above is active.
  posthog.startSessionRecording();
  isInitialized = true;
};

export const setAnalyticsContext = (nextContext: AnalyticsContext): void => {
  context = nextContext;
};

export const captureAnalyticsEvent = (event: string, properties: AnalyticsProperties = {}): void => {
  if (!isInitialized || !context || isBuyerShareRoute()) return;
  posthog.capture(event, { ...context, ...properties });
};
